import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './app/router';
import { useAuthStore } from './app/store/authStore';
import { ToastContainer } from './components/ui/Toast';
import { ApiError } from './services/apiClient';
import './i18n';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Agents work on flaky connections; one silent retry smooths over a
      // dropped request without making a genuine failure take forever to show.
      retry: (failureCount, error) => {
        // Never retry a client error — a 403 will not become a 200.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Writes are never retried automatically. The collection endpoint is
      // idempotent by design, but a blind retry of anything else could
      // duplicate a record.
      retry: false,
    },
  },
});

// Verify the stored session before the router renders, so a signed-in user
// does not flash the login screen on every reload.
void useAuthStore.getState().initialise();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ToastContainer />
    </QueryClientProvider>
  </StrictMode>,
);
